import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import '../../sass/pages/env-analyzer.scss';

type AlertType = 'success' | 'danger' | 'info' | 'warning';

interface CommitInfo {
  sha: string;
  shortSha: string;
  committerDate: string;
  authorDate: string;
  author: string;
  message: string;
}

interface DailyActivityEntry {
  date: string;
  newServices: string[];
  existingServicesUpdated: string[];
}

interface ImageRef {
  path: string;
  repository: string | null;
  tag: string | null;
}

interface DiffBlob {
  file: string;
  path: string;
  diffText: string;
  truncated: boolean;
}

interface ServiceWithDiffs {
  service: string;
  images: ImageRef[];
  chartVersion: string | null;
  diffs: DiffBlob[];
}

interface ImageTagChange {
  path: string;
  oldTag: string | null;
  newTag: string | null;
  repository: string | null;
}

interface ValuesChange {
  key: string;
  oldValue: unknown;
  newValue: unknown;
}

interface ChartMetadataChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

interface ChartDependencyChange {
  name: string;
  changeType: 'added' | 'removed' | 'changed';
  oldVersion?: string;
  newVersion?: string;
}

interface ChangedServiceRow {
  service: string;
  imageTagChanges: ImageTagChange[];
  valuesChanges: ValuesChange[];
  chartMetadataChanges: ChartMetadataChange[];
  chartDependencyChanges: ChartDependencyChange[];
  changeSummary: string[];
  diffs: DiffBlob[];
}

interface UnanalyzedFileChange {
  service: string;
  path: string;
  status: string;
}

interface ReportResponse {
  tenantPath: string;
  comparison: {
    baseCommit: CommitInfo | null;
    headCommit: CommitInfo;
    notes: string[];
  };
  dailyActivity: DailyActivityEntry[];
  addedServices: ServiceWithDiffs[];
  removedServices: ServiceWithDiffs[];
  imageOnlyChanges: ChangedServiceRow[];
  configOnlyChanges: ChangedServiceRow[];
  bothChanges: ChangedServiceRow[];
  unanalyzedFileChanges: UnanalyzedFileChange[];
}

interface StatusInfo {
  branch: string;
  tenantPath: string;
  headSha: string;
  headCommitDate: string;
  lastSyncedAt: string | null;
}

// <input type="datetime-local"> carries no timezone info. Per the UX
// requirement the two inputs are labeled "UTC" and their value is treated as
// ALREADY being UTC wall-clock time - converted to an ISO string by appending
// 'Z' directly. We deliberately never pass it through `new Date(value)`,
// which would apply the browser's local timezone instead.
function localInputToUtcIso(value: string): string | null {
  if (!value) return null;
  const withSeconds = value.length === 16 ? `${value}:00` : value; // "YYYY-MM-DDTHH:mm[:ss]"
  return `${withSeconds}Z`;
}

function utcNow(): string {
  return new Date().toISOString().slice(0, 16);
}

type PresetUnit = 'day' | 'week' | 'month';

function utcNowMinus(amount: number, unit: PresetUnit): string {
  const d = new Date();
  if (unit === 'day') d.setUTCDate(d.getUTCDate() - amount);
  if (unit === 'week') d.setUTCDate(d.getUTCDate() - amount * 7);
  if (unit === 'month') d.setUTCMonth(d.getUTCMonth() - amount);
  return d.toISOString().slice(0, 16);
}

const RANGE_PRESETS: { label: string; amount: number; unit: PresetUnit }[] = [
  { label: '1 day ago', amount: 1, unit: 'day' },
  { label: '2 days ago', amount: 2, unit: 'day' },
  { label: '1 week ago', amount: 1, unit: 'week' },
  { label: '1 month ago', amount: 1, unit: 'month' },
];

function formatUtc(iso: string | null | undefined): string {
  if (!iso) return '—';
  const withoutMillis = iso.replace(/\.\d+/, '');
  const withoutOffset = withoutMillis.replace(/(?:Z|\+00:00)$/, '');
  return `${withoutOffset.replace('T', ' ')} UTC`;
}

function formatTagList(changes: ImageTagChange[], key: 'oldTag' | 'newTag'): string {
  const values = changes.map(c => c[key] ?? '—');
  return values.length ? values.join(', ') : '—';
}

function chartVersionChange(row: ChangedServiceRow): ChartMetadataChange | undefined {
  return row.chartMetadataChanges.find(c => c.field === 'version');
}

// Diff lines colored by role, never by inventing new hues - text stays on
// text tokens, the +/- role is the only thing carrying color here.
function DiffBlockView({ diffs }: { diffs: DiffBlob[] }) {
  if (diffs.length === 0) return null;
  return (
    <>
      {diffs.map(d => (
        <div key={d.file} className="diff-block">
          <div className="diff-file-label">
            {d.file}
            {d.truncated && ' (truncated)'}
          </div>
          <pre className="diff-pre">
            {d.diffText.split('\n').map((line, i) => {
              let cls = '';
              if (line.startsWith('+') && !line.startsWith('+++')) cls = 'diff-add';
              else if (line.startsWith('-') && !line.startsWith('---')) cls = 'diff-remove';
              else if (line.startsWith('@@')) cls = 'diff-hunk';
              return (
                <div key={i} className={cls}>
                  {line}
                </div>
              );
            })}
          </pre>
        </div>
      ))}
    </>
  );
}

function ChangeSummaryList({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <ul className="change-summary-list">
      {lines.map((line, i) => (
        <li key={i}>
          <code>{line}</code>
        </li>
      ))}
    </ul>
  );
}

function ExpandableRow({
  cells,
  hasDetails,
  children,
}: {
  cells: React.ReactNode[];
  hasDetails: boolean;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className={hasDetails ? 'expandable-row' : ''}
        onClick={() => hasDetails && setOpen(o => !o)}
      >
        {cells.map((c, i) => (
          <td key={i}>{c}</td>
        ))}
        <td className="text-end text-muted expand-toggle-cell">{hasDetails ? (open ? '▾' : '▸') : ''}</td>
      </tr>
      {open && hasDetails && (
        <tr className="expandable-row-detail">
          <td colSpan={cells.length + 1}>{children}</td>
        </tr>
      )}
    </>
  );
}

// A plain SVG <rect rx=...> rounds all four corners uniformly, which would
// make a bar look like a capsule. The mark spec calls for a rounded data-end
// with a square baseline, so the topmost visible segment of each bar is drawn
// as a rounded-top-only path instead; any segment below it stays a square rect.
function roundedTopRectPath(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, height, width / 2));
  if (r === 0) {
    return `M${x},${y} h${width} v${height} h${-width} Z`;
  }
  return [
    `M${x},${y + height}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + width - r},${y}`,
    `Q${x + width},${y} ${x + width},${y + r}`,
    `L${x + width},${y + height}`,
    'Z',
  ].join(' ');
}

// Stacked bar chart: new services (blue, bottom) vs existing services updated
// (orange, on top). Palette validated via the dataviz skill's validator
// (blue #2a78d6 / orange #eb6834 - worst adjacent CVD deltaE 24.7, normal-vision 33.6).
const CHART_HEIGHT = 160;
const BAR_MAX_WIDTH = 24;
const SEGMENT_GAP = 2;

function DailyActivityChart({ data }: { data: DailyActivityEntry[] }) {
  const [hovered, setHovered] = useState<number | null>(null);

  const maxTotal = useMemo(
    () => Math.max(1, ...data.map(d => d.newServices.length + d.existingServicesUpdated.length)),
    [data]
  );

  if (data.length === 0) return null;

  const plotWidth = Math.max(320, data.length * 28);
  const barSlot = plotWidth / data.length;
  const barWidth = Math.min(BAR_MAX_WIDTH, barSlot - 6);

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((maxTotal / yTicks) * i));

  return (
    <div className="daily-activity-chart">
      <div className="chart-legend">
        <span className="legend-item">
          <span className="legend-swatch legend-swatch-new" /> New services
        </span>
        <span className="legend-item">
          <span className="legend-swatch legend-swatch-existing" /> Existing services updated
        </span>
      </div>
      <div className="chart-scroll">
        <svg
          width={plotWidth + 40}
          height={CHART_HEIGHT + 30}
          role="img"
          aria-label="Daily service activity: new services vs existing services updated"
        >
          {tickValues.map((tick, i) => {
            const y = CHART_HEIGHT - (tick / maxTotal) * CHART_HEIGHT + 10;
            return (
              <g key={i}>
                <line x1={30} x2={plotWidth + 30} y1={y} y2={y} className="chart-gridline" />
                <text x={26} y={y + 4} textAnchor="end" className="chart-axis-label">
                  {tick}
                </text>
              </g>
            );
          })}

          {data.map((entry, i) => {
            const total = entry.newServices.length + entry.existingServicesUpdated.length;
            const newHeight = (entry.newServices.length / maxTotal) * CHART_HEIGHT;
            const existingHeight = (entry.existingServicesUpdated.length / maxTotal) * CHART_HEIGHT;
            const x = 30 + i * barSlot + (barSlot - barWidth) / 2;
            const baselineY = CHART_HEIGHT + 10;
            const newY = baselineY - newHeight;
            const existingHasGap = newHeight > 0 && existingHeight > 0;
            const existingY = newY - existingHeight - (existingHasGap ? SEGMENT_GAP : 0);

            return (
              <g
                key={entry.date}
                className="chart-bar-group"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(h => (h === i ? null : h))}
              >
                <rect
                  x={x - 4}
                  y={10}
                  width={barWidth + 8}
                  height={CHART_HEIGHT}
                  fill="transparent"
                />
                {newHeight > 0 &&
                  (existingHeight > 0 ? (
                    <rect x={x} y={newY} width={barWidth} height={newHeight} className="chart-bar-new" />
                  ) : (
                    <path d={roundedTopRectPath(x, newY, barWidth, newHeight, 4)} className="chart-bar-new" />
                  ))}
                {existingHeight > 0 && (
                  <path
                    d={roundedTopRectPath(x, existingY, barWidth, existingHeight, 4)}
                    className="chart-bar-existing"
                  />
                )}
                {total === 0 && <rect x={x} y={baselineY - 1} width={barWidth} height={1} className="chart-bar-empty" />}
                {i % Math.ceil(data.length / 10 || 1) === 0 && (
                  <text x={x + barWidth / 2} y={CHART_HEIGHT + 24} textAnchor="middle" className="chart-axis-label">
                    {entry.date.slice(5)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {hovered !== null && (
          <div className="chart-tooltip">
            <strong>{data[hovered].date}</strong>
            <div>New: {data[hovered].newServices.join(', ') || 'none'}</div>
            <div>Updated: {data[hovered].existingServicesUpdated.join(', ') || 'none'}</div>
          </div>
        )}
      </div>
    </div>
  );
}

const EnvAnalyzer: React.FC = () => {
  const [statusInfo, setStatusInfo] = useState<StatusInfo | null>(null);
  const [rangeStartInput, setRangeStartInput] = useState('');
  const [rangeEndInput, setRangeEndInput] = useState('');
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<AlertType>('info');
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = (await __request(OpenAPI, {
        method: 'GET',
        url: '/api/env-analyzer/status/',
      })) as StatusInfo;
      setStatusInfo(response);
    } catch (error) {
      setStatusMessage(`Error loading status: ${String(error)}`);
      setStatusType('danger');
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      await __request(OpenAPI, { method: 'POST', url: '/api/env-analyzer/sync/' });
      setStatusMessage('Repository synced successfully.');
      setStatusType('success');
      await loadStatus();
    } catch (error) {
      setStatusMessage(`Error syncing repository: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setSyncing(false);
    }
  };

  const applyPreset = (amount: number, unit: PresetUnit) => {
    setRangeStartInput(utcNowMinus(amount, unit));
    setRangeEndInput(utcNow());
  };

  const generateReport = async () => {
    const start = localInputToUtcIso(rangeStartInput);
    const end = localInputToUtcIso(rangeEndInput);
    if (!start || !end) {
      setStatusMessage('Please select both a start and end date/time (UTC).');
      setStatusType('warning');
      return;
    }
    setGenerating(true);
    try {
      const response = (await __request(OpenAPI, {
        method: 'GET',
        url: '/api/env-analyzer/report/',
        query: { rangeStart: start, rangeEnd: end },
      })) as ReportResponse;
      setReport(response);
      const notes = response.comparison.notes || [];
      setStatusMessage(notes.length ? notes.join(' ') : 'Report generated successfully.');
      setStatusType(notes.length ? 'warning' : 'success');
    } catch (error) {
      setReport(null);
      setStatusMessage(`Error generating report: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setGenerating(false);
    }
  };

  const renderCommitCard = (label: string, commit: CommitInfo | null, highlight: boolean) => (
    <Col md={6}>
      <Card className={highlight ? 'commit-card commit-card-head' : 'commit-card'}>
        <Card.Header>
          <strong>{label}</strong>
        </Card.Header>
        <Card.Body>
          {commit ? (
            <>
              <div className="commit-message">
                <code>{commit.message}</code>
              </div>
              <div className="text-muted">
                <code>{commit.shortSha}</code> · {commit.author}
              </div>
              <div className="text-muted">{formatUtc(commit.committerDate)}</div>
            </>
          ) : (
            <span className="text-muted">No prior commit — comparing against an empty repository state.</span>
          )}
        </Card.Body>
      </Card>
    </Col>
  );

  const renderServiceImagesTable = (rows: ServiceWithDiffs[]) => (
    <Table hover responsive size="sm" className="env-analyzer-table">
      <thead>
        <tr>
          <th>Service</th>
          <th>Image</th>
          <th>Chart version</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <ExpandableRow
            key={row.service}
            hasDetails={row.diffs.length > 0}
            cells={[
              <strong key="s">{row.service}</strong>,
              row.images.length ? row.images.map(img => img.tag ?? '—').join(', ') : '—',
              row.chartVersion ?? '—',
            ]}
          >
            <DiffBlockView diffs={row.diffs} />
          </ExpandableRow>
        ))}
      </tbody>
    </Table>
  );

  const renderImageOnlyTable = (rows: ChangedServiceRow[]) => (
    <Table hover responsive size="sm" className="env-analyzer-table">
      <thead>
        <tr>
          <th>Service</th>
          <th>Old image</th>
          <th>New image</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <ExpandableRow
            key={row.service}
            hasDetails={row.diffs.length > 0 || row.changeSummary.length > 0}
            cells={[
              <strong key="s">{row.service}</strong>,
              formatTagList(row.imageTagChanges, 'oldTag'),
              formatTagList(row.imageTagChanges, 'newTag'),
            ]}
          >
            <ChangeSummaryList lines={row.changeSummary} />
            <DiffBlockView diffs={row.diffs} />
          </ExpandableRow>
        ))}
      </tbody>
    </Table>
  );

  const renderConfigOnlyTable = (rows: ChangedServiceRow[]) => (
    <Table hover responsive size="sm" className="env-analyzer-table">
      <thead>
        <tr>
          <th>Service</th>
          <th>Changes</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <ExpandableRow
            key={row.service}
            hasDetails={row.diffs.length > 0 || row.changeSummary.length > 0}
            cells={[<strong key="s">{row.service}</strong>, `${row.changeSummary.length} change(s)`]}
          >
            <ChangeSummaryList lines={row.changeSummary} />
            <DiffBlockView diffs={row.diffs} />
          </ExpandableRow>
        ))}
      </tbody>
    </Table>
  );

  const renderBothTable = (rows: ChangedServiceRow[]) => (
    <Table hover responsive size="sm" className="env-analyzer-table">
      <thead>
        <tr>
          <th>Service</th>
          <th>Old image</th>
          <th>New image</th>
          <th>Old chart version</th>
          <th>New chart version</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const versionChange = chartVersionChange(row);
          return (
            <ExpandableRow
              key={row.service}
              hasDetails={row.diffs.length > 0 || row.changeSummary.length > 0}
              cells={[
                <strong key="s">{row.service}</strong>,
                formatTagList(row.imageTagChanges, 'oldTag'),
                formatTagList(row.imageTagChanges, 'newTag'),
                versionChange?.oldValue != null ? String(versionChange.oldValue) : '—',
                versionChange?.newValue != null ? String(versionChange.newValue) : '—',
              ]}
            >
              <ChangeSummaryList lines={row.changeSummary} />
              <DiffBlockView diffs={row.diffs} />
            </ExpandableRow>
          );
        })}
      </tbody>
    </Table>
  );

  const emptyState = (text: string) => <p className="text-muted">{text}</p>;

  return (
    <div className="env-analyzer-container">
      <div className="env-analyzer-header">
        <h1>Env Analyzer</h1>
        {statusInfo?.lastSyncedAt && (
          <div className="text-muted synced-at-label">
            Synced at {formatUtc(statusInfo.lastSyncedAt)} ({statusInfo.headSha.slice(0, 7)})
          </div>
        )}
      </div>
      <p className="text-muted utc-note">All times are UTC (matches GitHub commit timestamps).</p>

      {statusMessage && (
        <Alert variant={statusType} dismissible onClose={() => setStatusMessage('')}>
          <strong>Status:</strong> {statusMessage}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end mb-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <strong>Since</strong>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={rangeStartInput}
                  onChange={e => setRangeStartInput(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md="auto" className="preset-buttons">
              {RANGE_PRESETS.map(p => (
                <Button
                  key={p.label}
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => applyPreset(p.amount, p.unit)}
                >
                  {p.label}
                </Button>
              ))}
            </Col>
          </Row>
          <Row className="align-items-end mb-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <strong>Until</strong>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={rangeEndInput}
                  onChange={e => setRangeEndInput(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button size="sm" variant="outline-secondary" onClick={() => setRangeEndInput(utcNow())}>
                Now
              </Button>
            </Col>
            <Col md="auto" className="ms-auto d-flex gap-2">
              <Button variant="outline-primary" onClick={runSync} disabled={syncing}>
                {syncing ? 'Syncing...' : 'Sync repo'}
              </Button>
              <Button onClick={generateReport} disabled={generating}>
                {generating ? 'Generating...' : 'Generate report'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {report && (
        <>
          <p className="text-muted analyzing-path-note">
            Analyzing changes under <span className="tenant-path-highlight">{report.tenantPath}</span>
          </p>

          <Row className="mb-4">
            {renderCommitCard('Base (start of range)', report.comparison.baseCommit, false)}
            {renderCommitCard('Head (end of range)', report.comparison.headCommit, true)}
          </Row>

          {report.dailyActivity.length > 0 && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Daily activity</h5>
                <p className="text-muted small">
                  Services with commits per day, new vs existing. Hover a bar for the per-service breakdown.
                </p>
                <DailyActivityChart data={report.dailyActivity} />
              </Card.Body>
            </Card>
          )}

          <h4>New services</h4>
          {report.addedServices.length === 0
            ? emptyState('No services were added in this range.')
            : renderServiceImagesTable(report.addedServices)}

          <h4>Removed services</h4>
          {report.removedServices.length === 0
            ? emptyState('No services were removed in this range.')
            : renderServiceImagesTable(report.removedServices)}

          <h4>Image version bump only</h4>
          {report.imageOnlyChanges.length === 0
            ? emptyState('None')
            : renderImageOnlyTable(report.imageOnlyChanges)}

          <h4>Config / chart version change only</h4>
          {report.configOnlyChanges.length === 0
            ? emptyState('None')
            : renderConfigOnlyTable(report.configOnlyChanges)}

          <h4>Both image bump and config/chart change</h4>
          {report.bothChanges.length === 0 ? emptyState('None') : renderBothTable(report.bothChanges)}

          {report.unanalyzedFileChanges.length > 0 && (
            <Alert variant="secondary" className="mt-3">
              <strong>Other changed files (not classified):</strong>
              <ul className="mb-0">
                {report.unanalyzedFileChanges.map((f, idx) => (
                  <li key={idx}>
                    {f.service}: {f.path} ({f.status})
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

export default EnvAnalyzer;
